import type { CourseQuizContent } from '../types'

export const leadWithAiContent: CourseQuizContent = {
  courseSlug: 'lead-with-ai-adapt-implement-transform-your-organization',
  courseName: 'Lead With AI',

  goalQuestion: {
    title: 'How do you plan to lead with AI at work today?',
    options: [
      {
        label:
          'By starting something myself. I don’t have a formal AI role yet.',
        value: 'no-mandate',
      },
      {
        label:
          'Through my team. I run a team or function and could launch a pilot.',
        value: 'team-function',
      },
      {
        label:
          'Across the company. I help set direction for company-wide change.',
        value: 'set-direction',
      },
    ],
  },

  levelQuestion: {
    title: 'What are you trying to do with AI at work right now?',
    options: [
      {
        label: 'Discovery: finding the AI opportunities worth betting on.',
        value: 'make-the-case',
      },
      {
        label:
          'Shipping something real: going from AI talk to a working pilot.',
        value: 'ship-something',
      },
      {
        label:
          'Getting the foundations right: data, governance, accountability.',
        value: 'foundations',
      },
      {
        label: 'Bringing people along: scaling adoption across teams.',
        value: 'bring-people',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'You’ll get hands-on with Microsoft Copilot and leave with your own AI Transformation Plan.',
    body: 'Map opportunities, get your data ready, and lead change responsibly, drawing on **Microsoft**’s decades of turning breakthrough tech into trusted tools.',
    image: 'https://static.masterclass.com/msft-interstitial-1.png',
    imageAlt: 'The AI Transformation Plan course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline: 'You don’t need a title to lead with AI.',
    body: 'This course is built for people driving AI at every level.\\\nWhether you’re pitching your first pilot or steering company strategy, you’ll build a plan that fits your role today and grows with you.',
    image: 'https://static.masterclass.com/msft-interstitial-2.png',
    imageAlt: 'Two colleagues collaborating over a laptop',
  },

  profiles: [
    {
      key: 'make-the-case',
      headline:
        'You want to lead with AI, but you’re unsure of how to identify the opportunities worth betting on',
      lead: 'Leadership keeps asking what your team is doing with AI, and the pressure is real. You’re not behind because you don’t care; you just need a place to start. This course gives you exactly that.',
      bullets: [
        {
          text: 'Map how AI is transforming your industry and **craft an AI Vision Statement** your leadership will actually read.',
        },
        {
          text: '**Score opportunities** against business outcomes, so the loudest idea stops beating the best one.',
        },
        {
          text: '**Define the KPIs** and **write the pilot pitch** that turns “we should look at AI” into a funded decision.',
        },
      ],
    },
    {
      key: 'ship-something',
      headline:
        'You want to move beyond just talking about AI to building a working pilot',
      lead: 'You’ve sat through enough AI theater: decks, demos, nothing shipped. What you need is a first pilot with a budget, a timeline, and a result. This course shows you how to get one.',
      bullets: [
        {
          text: '**Build a functional AI agent** yourself, with no code, so you understand what you’re asking your team to ship.',
        },
        {
          text: '**Estimate the ROI** before you spend a dollar, and use it to win stakeholder confidence.',
        },
        {
          text: '**Draft an AI Pilot Plan** with the timeline, budget, and milestones, ready to launch in weeks, not quarters.',
        },
      ],
    },
    {
      key: 'foundations',
      headline:
        'You want to be an AI leader, but care about getting the foundations right',
      lead: 'You understand that in your world, AI doesn’t ship until legal, security, and compliance say yes. You want to check those boxes before pushing AI initiatives forward. This course turns governance into the thing that speeds up adoption.',
      bullets: [
        {
          text: '**Write your Responsible AI Guiding Principles**, built on transparency, privacy, and security.',
        },
        {
          text: '**Design a Data Integration Blueprint** that makes your data AI-ready without compromising quality or safety.',
        },
        {
          text: '**Build an AI Oversight Strategy** that makes ownership, monitoring, and accountability explicit.',
        },
      ],
    },
    {
      key: 'bring-people',
      headline:
        'You want to lead with AI in a way that brings your people along',
      lead: 'The technology was never your problem. The habits, anxiety, and resistance between pilot and scale are. This course treats AI adoption as the transformational work it actually is.',
      bullets: [
        {
          text: '**Design the upskilling plan** that builds AI capability across your team, not just among the enthusiasts.',
        },
        {
          text: '**Craft an AI Change Management Playbook** for the resistance you know is coming.',
        },
        {
          text: 'Leave with a **complete AI Transformation Plan** covering your pilot, data strategy, oversight, and change approach in one document.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '16 to 20 weeks' },
    { key: '1to3h', weeks: '6 to 10 weeks' },
    { key: '3to5h', weeks: '4 to 5 weeks' },
    { key: '5plush', weeks: '3 weeks' },
  ],

  previewLessons: [
    {
      title: 'Lead With AI: Adapt, Implement & Transform Your Organization',
      mediaUuid: '1159f4aa-c82e-4cef-a913-ec14950bbdf4',
      image: 'https://static.masterclass.com/msft-video-1.png',
    },
    {
      title: 'Choosing the Right Work for AI',
      mediaUuid: 'be6d31e5-7b5f-42ea-a26e-cfbab2768011',
      image: 'https://static.masterclass.com/msft-video-2.png',
    },
  ],

  ctaHighlights: [
    'Taught by Microsoft executives.',
    'Create your AI Transformation Plan.',
    'Earn a shareable MasterClass Certificate.',
  ],
}
